import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { exemptionsService } from "@/features/settings/services/protestsExemptions.service";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import type { User } from "@/features/auth/model/authTypes";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import styles from "./ProtestDetailsModal.module.css";

interface ProtestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPropertyId?: string;
  propertyDetails?: PropertyApiResponse | null;
  user?: User | null;
}

const formatDateOnly = (dateStr?: string | null): string => {
  if (!dateStr) return "October 16, 2026";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "October 16, 2026";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "October 16, 2026 at 3:48 PM";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "October 16, 2026 at 3:48 PM";
  const datePart = d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} at ${timePart}`;
};

export const ProtestDetailsModal: React.FC<ProtestDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedPropertyId,
  propertyDetails,
  user,
}) => {
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(true);
  const [isTimelineOpen, setIsTimelineOpen] = useState(true);
  const [previewDoc, setPreviewDoc] = useState<{ title: string; url: string | null } | null>(null);

  const { data: protestsList } = useQuery({
    queryKey: ["protest-details-query", selectedPropertyId],
    queryFn: () => exemptionsService.getExemptions({ type: "protest", page: 1, page_size: 50 }),
    enabled: isOpen,
  });

  const matchedRecord = useMemo(() => {
    const payments = protestsList?.data?.payments;
    if (!payments || payments.length === 0) return null;
    return (
      payments.find(
        (p) =>
          String(p.propertyId) === String(selectedPropertyId) ||
          String(p.property_details?.property_id) === String(selectedPropertyId)
      ) ||
      payments[0] ||
      null
    );
  }, [protestsList, selectedPropertyId]);

  // 1. Protest ID & Status
  const rawId =
    matchedRecord?.payment_intent_id ||
    propertyDetails?.protest_id ||
    (matchedRecord?.id ? String(matchedRecord.id) : null) ||
    (selectedPropertyId
      ? `pi_3UR8DZD${selectedPropertyId.replace(/-/g, "").slice(0, 14)}`
      : "pi_3UR8DZDJVPEOLc881SJAa4SS");
  const protestIdDisplay = rawId.startsWith("#") ? rawId : `#${rawId}`;

  const rawStatus = (
    matchedRecord?.protest_status ||
    propertyDetails?.protest_status ||
    "submitted"
  ).toLowerCase();
  const statusLabel = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

  let statusClass = styles.statusPill;
  if (rawStatus === "approved") {
    statusClass = `${styles.statusPill} ${styles.statusApproved}`;
  } else if (rawStatus === "inprogress" || rawStatus === "inreview") {
    statusClass = `${styles.statusPill} ${styles.statusInReview}`;
  } else if (rawStatus === "rejected") {
    statusClass = `${styles.statusPill} ${styles.statusRejected}`;
  }

  // 2. Property Information
  const propId =
    propertyDetails?.property_id ||
    matchedRecord?.property_details?.property_id ||
    selectedPropertyId ||
    "680420b9-fcd9-4083-8cfe-6a161411a01a";

  const propAddress =
    propertyDetails?.address?.full_address ||
    [
      propertyDetails?.address?.street,
      propertyDetails?.address?.city,
      propertyDetails?.address?.state,
      propertyDetails?.address?.zip,
    ]
      .filter(Boolean)
      .join(", ") ||
    matchedRecord?.property_details?.full_address ||
    "4311 1/2 Ludwig Ln, Stafford, TX 77477";

  const propCity =
    propertyDetails?.address?.city || matchedRecord?.property_details?.city || "Stafford";

  const propState =
    propertyDetails?.address?.state || matchedRecord?.property_details?.state || "TX";

  const propZip = propertyDetails?.address?.zip || matchedRecord?.property_details?.zip || "77477";

  const propAdded = formatDateOnly(propertyDetails?.created_at || matchedRecord?.created_at);

  const rawPropType =
    propertyDetails?.property_type ||
    matchedRecord?.property_details?.property_type ||
    "residential";
  const propType = rawPropType
    .toLowerCase()
    .replace(/^propertytype\./i, "")
    .replace(/_/g, " ");

  // 3. Documents
  const protestDocs =
    matchedRecord?.property_protest_documents?.[0] ||
    matchedRecord?.property_protest_document?.[0] ||
    ((
      propertyDetails?.documents?.property_protest_documents as
        | Record<string, unknown>[]
        | undefined
    )?.[0] as Record<string, string | undefined> | undefined);

  const documents = [
    {
      title: "Andy App Agreement",
      url: protestDocs?.signed_andy_app_agreement_document_url || null,
    },
    {
      title: "Signed AOA Document",
      url: protestDocs?.signed_aoa || null,
    },
    {
      title: "Signed DTR Document",
      url: protestDocs?.signed_dtr || protestDocs?.signed_dtr_document_url || null,
    },
    {
      title: "Notice of Protest",
      url: protestDocs?.signed_notice_of_protest_document_url || null,
    },
    ...(matchedRecord?.refund_invoice_sign_doc || protestDocs?.refund_invoice_sign_doc
      ? [
          {
            title: "Refund Invoice Document",
            url:
              matchedRecord?.refund_invoice_sign_doc ||
              protestDocs?.refund_invoice_sign_doc ||
              null,
          },
        ]
      : []),
  ];

  const handleOpenDocument = (doc: { title: string; url: string | null }) => {
    setPreviewDoc(doc);
  };

  // 4. Timeline
  const timelineDate = formatDateTime(matchedRecord?.created_at || propertyDetails?.created_at);

  // 5. Contact Information
  const contactName =
    matchedRecord?.user_details?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "mahesh Godugu";

  const contactEmail =
    matchedRecord?.user_details?.email || user?.email || "mahesh@scienstechnologies.com";

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="protest-details-title"
    >
      <div className={styles.modalContainer}>
        {/* Top Bar with Back Arrow, Title, Close Button */}
        <div className={styles.modalTopBar}>
          <button type="button" className={styles.backBtn} onClick={onClose} aria-label="Go back">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span>Back</span>
          </button>
          <h2 id="protest-details-title" className={styles.modalTopTitle}>
            Protest Details
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className={styles.modalScrollContent}>
          {/* Card 1: Protest ID & Status */}
          <div className={`${styles.detailCard} ${styles.idStatusCard}`}>
            <div className={styles.idGroup}>
              <span className={styles.idLabel}>Protest ID</span>
              <h3 className={styles.idValue}>{protestIdDisplay}</h3>
            </div>
            <div className={statusClass}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{statusLabel}</span>
            </div>
          </div>

          {/* Card 2: Property Information */}
          <div className={styles.detailCard}>
            <div className={`${styles.cardHeaderRow} ${styles.cardHeaderRowNonClickable}`}>
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Property Information</h4>
              </div>
            </div>

            <div className={styles.infoGrid}>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property ID:</span>
                <span className={styles.infoVal}>{propId}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Address:</span>
                <span className={styles.infoVal}>{propAddress}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>City:</span>
                <span className={styles.infoVal}>{propCity}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>State:</span>
                <span className={styles.infoVal}>{propState}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>ZIP Code:</span>
                <span className={styles.infoVal}>{propZip}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property Added:</span>
                <span className={styles.infoVal}>{propAdded}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoKey}>Property Type:</span>
                <span className={styles.infoVal}>{propType}</span>
              </div>
            </div>
          </div>

          {/* Card 3: Documents Accordion */}
          <div className={styles.detailCard}>
            <div
              className={styles.cardHeaderRow}
              onClick={() => setIsDocumentsOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              aria-expanded={isDocumentsOpen}
            >
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Documents</h4>
              </div>
              <button
                type="button"
                className={`${styles.accordionChevron} ${!isDocumentsOpen ? styles.collapsed : ""}`}
                aria-label={isDocumentsOpen ? "Collapse documents" : "Expand documents"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            </div>

            {isDocumentsOpen && (
              <div className={styles.documentsList}>
                {documents.map((doc, idx) => (
                  <div
                    key={`${doc.title}-${idx}`}
                    className={styles.documentItem}
                    onClick={() => handleOpenDocument(doc)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.docItemLeft}>
                      <span className={styles.docIcon}>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </span>
                      <span className={styles.docTitle}>{doc.title}</span>
                    </div>
                    <span className={styles.docActionIcon}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Card 4: Timeline Accordion */}
          <div className={styles.detailCard}>
            <div
              className={styles.cardHeaderRow}
              onClick={() => setIsTimelineOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              aria-expanded={isTimelineOpen}
            >
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Timeline</h4>
              </div>
              <button
                type="button"
                className={`${styles.accordionChevron} ${!isTimelineOpen ? styles.collapsed : ""}`}
                aria-label={isTimelineOpen ? "Collapse timeline" : "Expand timeline"}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            </div>

            {isTimelineOpen && (
              <div className={styles.timelineContainer}>
                <div className={styles.timelineItem}>
                  <div className={styles.timelineNode}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </div>
                  <div className={styles.timelineDetails}>
                    <h5 className={styles.timelineMilestoneTitle}>Protest Submitted</h5>
                    <p className={styles.timelineMilestoneDesc}>
                      Your protest was successfully submitted for review
                    </p>
                    <span className={styles.timelineMilestoneDate}>{timelineDate}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Card 5: Contact Information */}
          <div className={styles.detailCard}>
            <div className={`${styles.cardHeaderRow} ${styles.cardHeaderRowNonClickable}`}>
              <div className={styles.cardHeaderLeft}>
                <span className={styles.cardHeaderIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </span>
                <h4 className={styles.cardHeaderTitle}>Contact Information</h4>
              </div>
            </div>

            <div className={styles.contactGrid}>
              <div className={styles.contactRow}>
                <span className={styles.contactKey}>Name:</span>
                <span className={styles.contactVal}>{contactName}</span>
              </div>
              <div className={styles.contactRow}>
                <span className={styles.contactKey}>Email:</span>
                <span className={styles.contactVal}>{contactEmail}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* In-App Document & Image Preview Modal */}
      <DocumentPreviewModal
        isOpen={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.title || "Document Preview"}
        url={previewDoc?.url || null}
      />
    </div>
  );
};
